import moment from "moment";
import { PermissionNames } from "src/models/modules";

export interface ReportInfo {
  id: number;
  code: REPORT_CODE;
  name: string;
  nameInNotification?: string;
  description?: string;
  isScheduled?: boolean;
  isAsync?: boolean;
  tier: ReportTier;
  isVisible: boolean;
}

export interface ReportTierSummary {
  id: number;
  tier: ReportTier;
  name: string;
  permissionName: PermissionNames;
  reportList: ReportInfo[];
}

export enum ReportTier {
  BASIC = "basic",
  PREMIUM = "premium",
  ADMIN_MANAGEMENT = "admin management",
  ACTIVITY_SUMMARY = "activity summary",
  REDEMPTION = "redemption",
  BLU_INTERNAL = "blu internal",
  ACCOR_REPORTS = "Accor Reports",
  PENDING_REWARD_REPORTS = "Pending Rewards Reports",
  MARKET_PLACE_REPORTS = "Marketplace Reports",
}

export enum REPORT_CODE {
  REWARD_TRANSACTIONS = "reward-transactions",
  ALL_CATALOGUE_REDEMPTION_TRANSACTIONS = "all-catalog-redemption-transactions",
  DETAILED_IN_STORE_REDEMPTION_TRANSACTIONS = "detailed-in-store-redemption-transactions",
  IN_STORE_REDEMPTION_TRANSACTIONS = "in-store-redemption-transactions",
  POINTS_REWARDED_PER_PARTNER_AND_USER = "points-rewarded-per-partner-user",
  VOUCHER_REDEMPTIONS = "redemption-vouchers",
  MEMBER_STATEMENTS = "member-statements",
  MEMBER_PERFORMANCE = "member-performance",
  REDEMPTION_OVERVIEW = "redemption-overview",
  FILE_UPLOAD_SUMMARY = "file-upload-summary",
  REDEMPTION_BY_ITEM = "redemption-by-item",
  EXPIRED_POINTS_DETAILS = "expired-points-details-report",
  TOP_EARNERS = "top-earners",
  SURVEY_PERFORMANCE = "survey-performance",
  CLO_CASHBACK_TRANSACTIONS = "clo-cashback-transactions",
  POINTS_REDEEMED_BY_CATEGORY = "points-redeemed-by-category",
  FAILED_PARTNER_REWARD_TRANSACTIONS = "failed-to-process-partner-reward-transactions",
  REDEMPTION_OF_PARTNER_ISSUED_POINTS = "redemption-of-partner-issued-points",
  PARTNER_MONTHLY_PERFORMANCE = "partner-monthly-performance",
  UPCOMING_POINTS_EXPIRATION = "upcoming-points-expiration",
  GENERIC_ALL_REDEMPTIONS = "generic-all-redemptions",
  MEMBERS_LIST = "members-list",
  BALANCE_PENDING_MEMBERS_LIST = "balance-pending-members-list",
  TOTAL_POINTS_PER_MEMBER = "total-points-per-member",
  REWARD_ONLY_TRANSACTIONS = "rewards-only-transactions",
  POINTS_REWARDED_BY_TRANSACTION = "points-rewarded-by-transaction",
  POINTS_REWARDED_BY_CATEGORY = "points-rewarded-by-category",
  POINTS_REDEEMED_BY_TRANSACTION = "points-redeemed-by-transaction",
  CUSTOMER_ENROLLMENT = "customer-enrollement",
  PARTNER_REWARD_TRANSACTIONS = "partner-reward-transactions",
  REDEMPTIONS = "redemption-report",
  REWARD_POINTS_ACCUMULATION_PER_PARTNER = "reward-points-accumulation-per-partner",
  MEMBERS_LIST_PER_SEGMENT = "members-list-per-segment",
  POINTS_REWARDED_PER_REWARD_PRODUCT = "points-rewarded",
  REDEMPTIONS_BY_MEMBER = "redemptions-by-member",
  REDEMPTIONS_OF_PARTNER_ISSUED_POINTS_PER_TRANSACTION = "redemptions-of-partner-issued-points-per-transaction",
  ALL_REDEMPTION_TRANSACTIONS = "all-redemption-transactions",
  TOP_REDEEMERS = "top-redeemers",
  STORE_COMPARISON = "store-comparison",
  ACCOUNTS_CLOSURE_POINTS_DEDUCTED = "accounts-closure-points-deducted",
  CATEGORY_COMPARISON = "category-comparison",
  POS_COMPARISON = "pos-comparison",
  PERIOD_COMPARISON = "period-comparison",
  AUDIT_TRAIL = "audit-trail",
  AUDIT_TRAIL_USERS = "audit-trail-users",
  ENROLLMENT = "enrollement-report",
  PARTNER_SUMMARY_PENDING_TRANSACTIONS = "partner-summary-pending-transactions",
  ACTIVE_USERS_PER_PARTNER = "active-users-per-partner",
  AUDIT_TRAIL_COMPREHENSIVE = "audit-trail-comprehensive",
  ADMINS_LIST = "admins-list",
  ENROLLMENT_SUMMARY = "enrollement-summary",
  PERIOD_TOTALS_BY_PARTNER = "period-totals-by-partner",
  PERIOD_TOTALS_BY_MEMBER = "period-totals-by-member",
  PERIOD_TOTALS_BY_BRANCH = "period-totals-by-branch",
  PERIOD_TOTALS_BY_NETWORK = "period-totals-by-network",
  BRANCH_POINTS_TRANSACTIONS = "branch-points-transactions",
  TRANSACTIONS = "transactions-report",
  BRANCH_TRANSACTIONS_IN_AMOUNT = "branch-transactions-in-amount",
  LOYALTY_PROGRAM_COMPARISON = "loyalty-program-comparison",
  SEGMENT_COMPARISON = "segment-comparison",
  COUNTRY_COMPARISON = "country-comparison",
  ITEM_COMPARISON = "item-comparison",
  STAFF_COMPARISON = "staff-comparison",
  ACCESS_RIGHTS = "access-rights",
  REMOVED_CUSTOMERS_OR_PRODUCTS = "removed-customers-or-products",
  BLOCKED_MEMBER_ACCOUNTS = "blocked-member-accounts",
  MEMBERS_WITH_DEFAULT_PIN_CODE = "members-with-default-pin-code",
  TOTAL_BALANCE_RECONCILIATION = "total-balance-reconciliation",
  REDEMPTIONS_BY_ID = "redemptions-report-by-id",
  POINTS_REDEEMED_AT_SUPPLIER = "points-redeemed-at-supplier",
  MEMBER_BALANCE_VS_POINTS_REWARDED = "member-balance-vs-points-rewarded",
  BRANCH_POINTS_TRANSACTIONS_BY_POSTED_DATE = "branch-points-transactions-by-posted-date",
  REDEMPTION_BY_PARTNER_USED_POINTS = "redemption-by-partner-used-points-vs-others",
  REDEMPTION_BY_PARTNER_USED_POINTS_BY_ITEM = "redemption-by-partner-used-points-by-item",
  ONLINE_VOUCHERS_REDEMPTION = "online-vouchers-redemption",
  CATALOGUE_LISTING_PER_PARTNER = "catalogue-listing-per-partner",
  SUPPLIER_INVOICE_RECONCILIATION = "supplier-invoice-reconciliation",
  REDEMPTION_BY_PARTNER = "redemption-by-partner",
  ALINMA_MONTHLY_REDEMPTION = "alinma-monthly-redemption",
  ITEM_REDEMPTION_BY_PARTNER = "item-redemption-by-partner",
  PROFIT_AND_LOSS_BY_SUPPLIER = "profile-and-loss-by-supplier",
  PROFIT_AND_LOSS_BY_PARTNER = "items-profit-and-loss",
  OFFLINE_VOUCHER_REPORT = "offline-voucher-report",
  CATALOGUE_REDEMPTION_DETAILS = "catalogue-redemption-details",
  CATALOGUE_ITEMS_PER_PARTNER = "catalogue-items-per-partner",
  POINTS_REWARDED_REPORT_FOR_BUSINESS = "points-rewarded-report-for-business",
  POINTS_REWARDED_REPORT_FOR_FINANCE = "points-rewarded-report-for-finance",
  ADHOC_REDEMPTION_TRANSACTIONS = "adhoc-redemption-transactions",
  CARBON_OFFSET_TRANSACTIONS = "carbon-offset-redemptions",
  POINTS_CONVERSION_REDEMPTION = "points-conversion-redemption",
  PENDING_NEGATIVE_REWARD_TRANSACTIONS = "pending-negative-reward-transactions",
  PENDING_REVERSAL_REWARD_TRANSACTIONS = "pending-reversal-reward-transactions",
  PAY_WITH_POINTS = "pay-with-points",
  PERFORMANCE = "performance",
  POINTS_REWARDED_PER_MEMBER = "points-rewarded-per-member",
  GENERIC_MARKET_PLACE_ORDERS = "generic-marketplace-orders",
  ALL_CATALOG_MARKET_PLACE_ORDERS = "all-catalog-marketplace-orders",
  MARKET_PLACE_AFFILIATE_PROGRAM_ORDERS = "marketplace-affiliate-program-orders",
  ASCENDA_REDEMPTIONS = "ascenda",
  PROFIT_AND_LOSS_FOR_ITEMS = "profit-and-lost-for-items",
  PARTNER_POINTS_USED_BY_ITEM = "partner-points-used-by-item",
  DIGITAL_VALUE_TRANSACTIONS = "digital-value-redemption-transactions",
  VOUCHER_REDEMPTION_AND_USAGE = "voucher-redemption-and-usage",
  ADHOC_POINTS_ADJUSTMENT = "adhoc-points-adjustment",
  CASHBACK_REDEMPTION_TRANSACTIONS = "cashback-redemption-transactions",
  POINTS_SUMMARY_REPORT = "points-summary",
  ONLINE_VOUCHERS_REDEMPTION_MARKETPLACE_PARTNERS = "online-vouchers-redemption-marketplace-partners",
}

export enum ReportIds {
  BRANCH_POINTS_TRANSACTIONS = 1,
  MEMBER_LIST = 2,
  TRANSACTIONS = 3,
  MEMBER_STATEMENTS = 4,
  BRANCH_TRANSACTIONS_IN_AMOUNT = 5,
  TOP_POINTS_EARNERS = 6,
  TOP_REDEEMERS = 7,
  STORE_COMPARISON = 8,
  LOYALTY_PROGRAM_COMPARISON = 9,
  SEGMENT_COMPARISON = 10,
  COUNTRY_COMPARISON = 11,
  CATEGORY_COMPARISON = 12,
  ITEM_COMPARISON = 13,
  POS_COMPARISON = 14,
  STAFF_COMPARISON = 15,
  AUDIT_TRAIL = 16,
  ACCESS_RIGHT = 17,
  REMOVED_CUSTOMERS_OR_PRODUCTS = 18,
  BLOCKED_MEMBER_ACCOUNTS = 19,
  MEMBERS_WITH_DEFAULT_PIN_CODES = 20,
  TOTAL_BALANCE_RECONCILIATION = 21,
  PERIOD_TOTALS_BY_PARTNER = 22,
  PERIOD_TOTALS_BY_MEMBER = 23,
  PERIOD_TOTALS_BY_BRANCH = 24,
  PERIOD_TOTALS_BY_NETWORK = 25,
  REDEMPTION_OF_PARTNER_ISSUED_POINTS = 26,
  REDEMPTIONS_BY_MEMBER = 27,
  REDEMPTIONS_BY_ID = 28,
  TOTAL_POINTS_PER_MEMBER = 29,
  AUDIT_TRAIL_USERS = 30,
  POINTS_REDEEMED_AT_SUPPLIER = 31,
  POINTS_REDEEMED_BY_CATEGORY = 34,
  REDEMPTION_OF_PARTNER_ISSUED_POINTS_PER_TRANSACTION = 36,
  MEMBER_BALANCE_VS_POINTS_REWARDED = 37,
  REWARD_ONLY_TRANSACTIONS = 39,
  ALL_REDEMPTION_TRANSACTIONS = 40,
  ENROLLMENT = 42,
  PERIOD_COMPARISON = 43,
  POINTS_REWARDED_BY_TRANSACTION = 44,
  POINTS_REDEEMED_BY_TRANSACTION = 45,
  POINTS_REWARDED_BY_CATEGORY = 46,
  CUSTOMER_ENROLLMENT = 50,
  BRANCH_POINTS_TRANSACTIONS_BY_POSTED_DATE = 54,
  PARTNER_REWARD_TRANSACTIONS = 55,
  REDEMPTIONS = 59,
  REDEMPTION_BY_PARTNER_USED_POINTS = 60,
  REDEMPTION_BY_PARTNER_USED_POINTS_BY_ITEM = 61,
  POINTS_REWARDED_PER_PARTNER_AND_USER = 63,
  ONLINE_VOUCHER_REDEMPTION = 64,
  FILE_UPLOAD_SUMMARY = 65,
  CATALOGUE_LISTING_PER_PARTNER = 66,
  SUPPLIER_INVOICE_RECONCILIATION = 70,
  REDEMPTION_BY_PARTNER = 71,
  ALINMA_MONTHLY_REDEMPTION = 72,
  ITEM_REDEMPTION_BY_PARTNER = 73,
  PROFIT_AND_LOSS_BY_SUPPLIER = 74,
  ACCOUNTS_CLOSURE_POINTS_DEDUCTED = 75,
  EXPIRED_POINTS_DETAILS = 76,
  REWARD_POINTS_ACCUMULATION_PER_PARTNER_PER_PERIOD = 77,
  PARTNER_SUMMARY_PENDING_TRANSACTIONS = 78,
  ACTIVE_USERS_PER_PARTNER = 79,
  OFFLINE_VOUCHER_REDEMPTION = 80,
  REWARD_TRANSACTION_DETAILS = 81,
  AUDIT_TRAIL_COMPREHENSIVE = 82,
  UPCOMING_POINTS_EXPIRATION = 83,
  CLO_CASHBACK_TRANSACTIONS = 84,
  MEMBERS_LIST_PER_SEGMENT = 85,
  CATALOGUE_REDEMPTION_DETAILS = 86,
  CATALOGUE_ITEMS_PER_PARTNER = 87,
  GENERIC_ALL_REDEMPTIONS = 88,
  FINANCE_POINTS_REWARDED = 89,
  POINTS_REWARDED_BUSINESS = 90,
  ENROLLMENT_SUMMARY = 91,
  POINTS_CONVERSION_REDEMPTIONS = 92,
  ALL_CATALOGUE_REDEMPTION_TRANSACTIONS = 93,
  DETAILED_IN_STORE_REDEMPTION_TRANSACTIONS = 94,
  IN_STORE_REDEMPTION_TRANSACTIONS = 95,
  VOUCHER_REDEMPTIONS = 96,
  REDEMPTIONS_OVERVIEW = 97,
  REDEMPTION_BY_ITEM = 98,
  FAILED_PARTNER_REWARD_TRANSACTIONS = 99,
  POINTS_REWARDED_PER_REWARD_PRODUCT = 100,
  PARTNER_MONTHLY_PERFORMANCE = 102,
  PROFIT_AND_LOSS_BY_PARTNER = 103,
  PAY_WITH_POINTS = 104,
  ADHOC_REDEMPTION_TRANSACTIONS = 105,
  ADMINS_LIST = 106,
  CARBON_OFFSET_REDEMPTIONS = 107,
  PERFORMANCE = 108,
  PENDING_NEGATIVE_REWARD_TRANSACTIONS = 109,
  BALANCE_PENDING_MEMBERS_LIST = 110,
  POINTS_REWARDED_PER_MEMBER = 111,
  GENERIC_MARKET_PLACE_ORDERS = 112,
  PENDING_REVERSAL_REWARD_TRANSACTIONS = 113,
  ALL_CATALOG_MARKET_PLACE_ORDERS = 114,
  MARKET_PLACE_AFFILIATE_PROGRAM_ORDERS = 115,
  ASCENDA_REDEMPTIONS = 116,
  PROFIT_AND_LOSS_FOR_ITEMS = 117,
  PARTNER_POINTS_USED_BY_ITEM = 118,
  DIGITAL_VALUE_TRANSACTIONS = 119,
  VOUCHER_REDEMPTION_AND_USAGE = 120,
  MEMBER_PERFORMANCE = 121,
  ADHOC_POINTS_ADJUSTMENT = 122,
  CASHBACK_REDEMPTION_TRANSACTIONS = 123,
  POINTS_SUMMARY_REPORT = 124,
  SURVEY_PERFORMANCE = 125,
  ONLINE_VOUCHERS_REDEMPTION_MARKETPLACE_PARTNERS = 126,
}

export const ReportInfoMap: Map<ReportIds, ReportInfo> = new Map<
  ReportIds,
  ReportInfo
>([
  [
    ReportIds.MEMBER_LIST,
    {
      id: ReportIds.MEMBER_LIST,
      code: REPORT_CODE.MEMBERS_LIST,
      name: "Members List",
      description: "This report shows the list of all Members",
      tier: ReportTier.BASIC,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.MEMBER_STATEMENTS,
    {
      id: ReportIds.MEMBER_STATEMENTS,
      code: REPORT_CODE.MEMBER_STATEMENTS,
      name: "Members Transactions",
      description:
        "The report shows all redemptions and rewards transactions per member during a specified period.",
      isScheduled: true,
      isAsync: true,
      tier: ReportTier.BASIC,
      isVisible: true,
    },
  ],
  [
    ReportIds.TOTAL_POINTS_PER_MEMBER,
    {
      id: ReportIds.TOTAL_POINTS_PER_MEMBER,
      name: "Total Points per Member",
      description: "This report shows the balances per user",
      code: REPORT_CODE.TOTAL_POINTS_PER_MEMBER,
      tier: ReportTier.BASIC,
      isVisible: false,
    },
  ],
  [
    ReportIds.REWARD_ONLY_TRANSACTIONS,
    {
      id: ReportIds.REWARD_ONLY_TRANSACTIONS,
      code: REPORT_CODE.REWARD_ONLY_TRANSACTIONS,
      name: "Rewards only Transactions",
      description:
        "This report shows the reward transactions executed in a period of time along with each transaction’s related loyalty event, original amount and currency on a partner or member level.",
      tier: ReportTier.BASIC,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.POINTS_REWARDED_BY_TRANSACTION,
    {
      id: ReportIds.POINTS_REWARDED_BY_TRANSACTION,
      code: REPORT_CODE.POINTS_REWARDED_BY_TRANSACTION,
      name: "Points Rewarded Report (by Transaction)",
      description: "Points Rewarded Report (by Transaction)",
      tier: ReportTier.BASIC,
      isVisible: false,
    },
  ],
  [
    ReportIds.POINTS_REWARDED_BY_CATEGORY,
    {
      id: ReportIds.POINTS_REWARDED_BY_CATEGORY,
      code: REPORT_CODE.POINTS_REWARDED_BY_CATEGORY,
      name: "Points Rewarded Report (by Category)",
      description: "Points Rewarded Report (by Category)",
      tier: ReportTier.BASIC,
      isVisible: false,
    },
  ],
  [
    ReportIds.POINTS_REDEEMED_BY_TRANSACTION,
    {
      id: ReportIds.POINTS_REDEEMED_BY_TRANSACTION,
      code: REPORT_CODE.POINTS_REDEEMED_BY_TRANSACTION,
      name: "Points Redeemed Report (by Transaction)",
      description: "Points Redeemed Report (by Transaction)",
      tier: ReportTier.BASIC,
      isVisible: false,
    },
  ],
  [
    ReportIds.CUSTOMER_ENROLLMENT,
    {
      id: ReportIds.CUSTOMER_ENROLLMENT,
      code: REPORT_CODE.CUSTOMER_ENROLLMENT,
      name: "Customer Enrollment Report",
      description: "Customer Enrollment Report",
      tier: ReportTier.BASIC,
      isVisible: false,
    },
  ],
  [
    ReportIds.PARTNER_REWARD_TRANSACTIONS,
    {
      id: ReportIds.PARTNER_REWARD_TRANSACTIONS,
      code: REPORT_CODE.PARTNER_REWARD_TRANSACTIONS,
      name: "Partner Reward Transactions",
      description: "Partner Reward Transactions",
      tier: ReportTier.BASIC,
      isVisible: false,
    },
  ],
  [
    ReportIds.REDEMPTIONS,
    {
      id: ReportIds.REDEMPTIONS,
      code: REPORT_CODE.REDEMPTIONS,
      name: "Redemption",
      description: "Redemption report",
      tier: ReportTier.BASIC,
      isVisible: false,
    },
  ],
  [
    ReportIds.POINTS_REWARDED_PER_PARTNER_AND_USER,
    {
      id: ReportIds.POINTS_REWARDED_PER_PARTNER_AND_USER,
      code: REPORT_CODE.POINTS_REWARDED_PER_PARTNER_AND_USER,
      name: "Points Rewarded per Partner and User",
      description: "Points Rewarded per Partner and User",
      isScheduled: false,
      tier: ReportTier.BASIC,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.REWARD_TRANSACTION_DETAILS,
    {
      id: ReportIds.REWARD_TRANSACTION_DETAILS,
      code: REPORT_CODE.REWARD_TRANSACTIONS,
      name: "Details Reward Transactions Report",
      nameInNotification: "Details Reward Transactions",
      description: "Detailed overview report for all reward transactions",
      isScheduled: true,
      tier: ReportTier.BASIC,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.REWARD_POINTS_ACCUMULATION_PER_PARTNER_PER_PERIOD,
    {
      id: ReportIds.REWARD_POINTS_ACCUMULATION_PER_PARTNER_PER_PERIOD,
      code: REPORT_CODE.REWARD_POINTS_ACCUMULATION_PER_PARTNER,
      name: "Reward Points Accumulation per Partner per Period (by file)",
      description:
        "Reward Points Accumulation per Partner per Period (by file)",
      tier: ReportTier.BASIC,
      isVisible: false,
    },
  ],
  [
    ReportIds.MEMBERS_LIST_PER_SEGMENT,
    {
      id: ReportIds.MEMBERS_LIST_PER_SEGMENT,
      code: REPORT_CODE.MEMBERS_LIST_PER_SEGMENT,
      name: "Members List Per Segment",
      description:
        "The report shows the list of members per segment in details as per the chosen partner and segment.",
      tier: ReportTier.BASIC,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.POINTS_REWARDED_PER_REWARD_PRODUCT,
    {
      id: ReportIds.POINTS_REWARDED_PER_REWARD_PRODUCT,
      code: REPORT_CODE.POINTS_REWARDED_PER_REWARD_PRODUCT,
      name: "Total Points Rewarded per Reward Product",
      description:
        "This report shows a summary of the points rewarded per reward product for a specific time period, network, and partner(s).",
      tier: ReportTier.BASIC,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.PARTNER_MONTHLY_PERFORMANCE,
    {
      id: ReportIds.PARTNER_MONTHLY_PERFORMANCE,
      code: REPORT_CODE.PARTNER_MONTHLY_PERFORMANCE,
      name: "Partner Monthly Performance",
      description:
        "Monthly report offering an overview of points issued, redeemed and expired on user level.",
      tier: ReportTier.BASIC,
      isScheduled: true,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.POINTS_REWARDED_PER_MEMBER,
    {
      id: ReportIds.POINTS_REWARDED_PER_MEMBER,
      code: REPORT_CODE.POINTS_REWARDED_PER_MEMBER,
      name: "Points Rewarded Per Member",
      description:
        "This report shows the total points rewarded within a period of time per member on a partner or member level.",
      tier: ReportTier.BASIC,
      isScheduled: true,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.PARTNER_POINTS_USED_BY_ITEM,
    {
      id: ReportIds.PARTNER_POINTS_USED_BY_ITEM,
      code: REPORT_CODE.PARTNER_POINTS_USED_BY_ITEM,
      name: "Partner Points used by Item",
      description: "This report shows how rewards were spent for which items.",
      tier: ReportTier.BASIC,
      isScheduled: true,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.PERFORMANCE,
    {
      id: ReportIds.PERFORMANCE,
      code: REPORT_CODE.PERFORMANCE,
      name: "Performance Report per Partner",
      description:
        "A report that provides a monthly overview to monitor performance at the partner level",
      isScheduled: true,
      isAsync: true,
      tier: ReportTier.BASIC,
      isVisible: true,
    },
  ],
  [
    ReportIds.MEMBER_PERFORMANCE,
    {
      id: ReportIds.MEMBER_PERFORMANCE,
      code: REPORT_CODE.MEMBER_PERFORMANCE,
      name: "Member Performance Report",
      description:
        "This report shows on a user level a summary of loyalty activity, displaying points issued, redeemed, expired, and the opening and closing balance for each member.",
      tier: ReportTier.BASIC,
      isScheduled: false,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.POINTS_REDEEMED_BY_CATEGORY,
    {
      id: ReportIds.POINTS_REDEEMED_BY_CATEGORY,
      code: REPORT_CODE.POINTS_REDEEMED_BY_CATEGORY,
      name: "Points Redeemed by Category",
      description:
        "The report will show the points redeemed by redemption category",
      isScheduled: true,
      isAsync: true,
      tier: ReportTier.REDEMPTION,
      isVisible: true,
    },
  ],
  [
    ReportIds.CLO_CASHBACK_TRANSACTIONS,
    {
      id: ReportIds.CLO_CASHBACK_TRANSACTIONS,
      code: REPORT_CODE.CLO_CASHBACK_TRANSACTIONS,
      name: "CLO Cashback Transactions",
      description: "This report shows the CLO cashback transactions",
      isScheduled: true,
      tier: ReportTier.REDEMPTION,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.IN_STORE_REDEMPTION_TRANSACTIONS,
    {
      id: ReportIds.IN_STORE_REDEMPTION_TRANSACTIONS,
      code: REPORT_CODE.IN_STORE_REDEMPTION_TRANSACTIONS,
      name: "In-Store Redemption Transactions Report",
      nameInNotification: "In-Store Redemption Transactions",
      description:
        "This report shows all redemption and reversal transactions that took place between two time frames at specific merchants",
      isScheduled: true,
      isAsync: true,
      tier: ReportTier.REDEMPTION,
      isVisible: true,
    },
  ],
  [
    ReportIds.REDEMPTION_OF_PARTNER_ISSUED_POINTS,
    {
      id: ReportIds.REDEMPTION_OF_PARTNER_ISSUED_POINTS,
      code: REPORT_CODE.REDEMPTION_OF_PARTNER_ISSUED_POINTS,
      name: "Redemption of Partner Issued Points",
      isScheduled: true,
      description:
        "This report shows the redemption on the partner rewarded points",
      tier: ReportTier.REDEMPTION,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.ALL_CATALOGUE_REDEMPTION_TRANSACTIONS,
    {
      id: ReportIds.ALL_CATALOGUE_REDEMPTION_TRANSACTIONS,
      code: REPORT_CODE.ALL_CATALOGUE_REDEMPTION_TRANSACTIONS,
      name: "All Catalog Redemption Transactions",
      description:
        "This report shows the details of the redemption transactions executed in a certain period of time on the catalog page of the program",
      isScheduled: true,
      tier: ReportTier.REDEMPTION,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.REDEMPTIONS_BY_MEMBER,
    {
      id: ReportIds.REDEMPTIONS_BY_MEMBER,
      code: REPORT_CODE.REDEMPTIONS_BY_MEMBER,
      name: "Redemptions by Member",
      description:
        "This report shows the redemptions made by a member from partner issued points.",
      tier: ReportTier.REDEMPTION,
      isVisible: false,
    },
  ],
  [
    ReportIds.REDEMPTION_OF_PARTNER_ISSUED_POINTS_PER_TRANSACTION,
    {
      id: ReportIds.REDEMPTION_OF_PARTNER_ISSUED_POINTS_PER_TRANSACTION,
      code: REPORT_CODE.REDEMPTIONS_OF_PARTNER_ISSUED_POINTS_PER_TRANSACTION,
      name: "Redemption of Partner Issued Points per Transaction",
      description: "Redemption of Partner Issued Points per Transaction",
      tier: ReportTier.REDEMPTION,
      isVisible: false,
    },
  ],
  [
    ReportIds.ALL_REDEMPTION_TRANSACTIONS,
    {
      id: ReportIds.ALL_REDEMPTION_TRANSACTIONS,
      code: REPORT_CODE.ALL_REDEMPTION_TRANSACTIONS,
      name: "All Redemption Transactions",
      description:
        "This report shows the redemption transactions executed in a\r\ncertain period of time. It includes the type of redemption along with the item/service redeemed.",
      tier: ReportTier.REDEMPTION,
      isVisible: false,
    },
  ],
  [
    ReportIds.GENERIC_ALL_REDEMPTIONS,
    {
      id: ReportIds.GENERIC_ALL_REDEMPTIONS,
      code: REPORT_CODE.GENERIC_ALL_REDEMPTIONS,
      name: "Generic All Redemptions",
      description:
        "This report shows the redemption transactions executed in certain period of time",
      tier: ReportTier.REDEMPTION,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.POINTS_CONVERSION_REDEMPTIONS,
    {
      id: ReportIds.POINTS_CONVERSION_REDEMPTIONS,
      code: REPORT_CODE.POINTS_CONVERSION_REDEMPTION,
      name: "Points Conversion Redemptions Report",
      nameInNotification: "Points Conversion Redemptions",
      description:
        "This report shows the points conversion redemptions executed in a period of time.  It includes the points redeemed along with the transaction's details",
      tier: ReportTier.REDEMPTION,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.PAY_WITH_POINTS,
    {
      id: ReportIds.PAY_WITH_POINTS,
      code: REPORT_CODE.PAY_WITH_POINTS,
      name: "Pay with Points Report",
      description:
        "This report shows the points redeemed via VISA Pay With Points that took place between two time frames for a specific partner.",
      isScheduled: true,
      isAsync: true,
      tier: ReportTier.REDEMPTION,
      isVisible: false,
    },
  ],
  [
    ReportIds.ADHOC_REDEMPTION_TRANSACTIONS,
    {
      id: ReportIds.ADHOC_REDEMPTION_TRANSACTIONS,
      code: REPORT_CODE.ADHOC_REDEMPTION_TRANSACTIONS,
      name: "Adhoc Redemption Transactions",
      description:
        "This report shows the adhoc points redeemed that took place for a specific partner.",
      isScheduled: false,
      isAsync: true,
      tier: ReportTier.REDEMPTION,
      isVisible: true,
    },
  ],
  [
    ReportIds.CARBON_OFFSET_REDEMPTIONS,
    {
      id: ReportIds.CARBON_OFFSET_REDEMPTIONS,
      code: REPORT_CODE.CARBON_OFFSET_TRANSACTIONS,
      name: "Carbon Offset Redemption Transactions",
      description:
        "This report shows the Carbon Offset redemptions that took place in a certain period of time.",
      isScheduled: false,
      isAsync: true,
      tier: ReportTier.REDEMPTION,
      isVisible: true,
    },
  ],
  [
    ReportIds.ASCENDA_REDEMPTIONS,
    {
      id: ReportIds.ASCENDA_REDEMPTIONS,
      code: REPORT_CODE.ASCENDA_REDEMPTIONS,
      name: "Ascenda Report",
      description:
        "This report shows the Ascenda redemptions that took place in a certain period of time.",
      isScheduled: false,
      isAsync: true,
      tier: ReportTier.REDEMPTION,
      isVisible: true,
    },
  ],
  [
    ReportIds.DIGITAL_VALUE_TRANSACTIONS,
    {
      id: ReportIds.DIGITAL_VALUE_TRANSACTIONS,
      code: REPORT_CODE.DIGITAL_VALUE_TRANSACTIONS,
      name: "Mobile & bill payments Redemption Transactions",
      description:
        "This report shows the mobile & bill payments redemptions executed in a period of time. It includes the points redeemed along with the transaction's details.",
      isScheduled: false,
      isAsync: true,
      tier: ReportTier.REDEMPTION,
      isVisible: true,
    },
  ],
  [
    ReportIds.VOUCHER_REDEMPTION_AND_USAGE,
    {
      id: ReportIds.VOUCHER_REDEMPTION_AND_USAGE,
      code: REPORT_CODE.VOUCHER_REDEMPTION_AND_USAGE,
      name: "Voucher Redemption and Usage Report",
      description:
        "This report shows the voucher redemption and usage report over a period of time.",
      isScheduled: false,
      isAsync: true,
      tier: ReportTier.REDEMPTION,
      isVisible: true,
    },
  ],
  [
    ReportIds.CASHBACK_REDEMPTION_TRANSACTIONS,
    {
      id: ReportIds.CASHBACK_REDEMPTION_TRANSACTIONS,
      code: REPORT_CODE.CASHBACK_REDEMPTION_TRANSACTIONS,
      name: "Cashback Redemption Transactions",
      description:
        "This report shows the cashback redemptions executed in a period of time. It includes the points redeemed along with the transaction's details.",
      isScheduled: false,
      isAsync: true,
      tier: ReportTier.REDEMPTION,
      isVisible: true,
    },
  ],
  [
    ReportIds.TOP_POINTS_EARNERS,
    {
      id: ReportIds.TOP_POINTS_EARNERS,
      code: REPORT_CODE.TOP_EARNERS,
      name: "Top Points Earners",
      description: "Report on top earners in points",
      isScheduled: true,
      tier: ReportTier.PREMIUM,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.TOP_REDEEMERS,
    {
      id: ReportIds.TOP_REDEEMERS,
      code: REPORT_CODE.TOP_REDEEMERS,
      name: "Top Redeemers",
      description: "Report on top redeemers in points",
      tier: ReportTier.PREMIUM,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.FILE_UPLOAD_SUMMARY,
    {
      id: ReportIds.FILE_UPLOAD_SUMMARY,
      code: REPORT_CODE.FILE_UPLOAD_SUMMARY,
      name: "File Upload Summary Report",
      nameInNotification: "File Upload Summary",
      description: "Summary on the files uploaded by partner",
      tier: ReportTier.PREMIUM,
      isAsync: true,
      isScheduled: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.STORE_COMPARISON,
    {
      id: ReportIds.STORE_COMPARISON,
      code: REPORT_CODE.STORE_COMPARISON,
      name: "Store Comparison",
      description: "Multiple stores loyalty performance comparison",
      tier: ReportTier.PREMIUM,
      isVisible: false,
    },
  ],
  [
    ReportIds.ACCOUNTS_CLOSURE_POINTS_DEDUCTED,
    {
      id: ReportIds.ACCOUNTS_CLOSURE_POINTS_DEDUCTED,
      code: REPORT_CODE.ACCOUNTS_CLOSURE_POINTS_DEDUCTED,
      name: "Accounts Closure Points Deducted",
      description:
        "Summary on the points deducted from the customer's account due to account closure.",
      tier: ReportTier.PREMIUM,
      isVisible: true,
    },
  ],
  [
    ReportIds.EXPIRED_POINTS_DETAILS,
    {
      id: ReportIds.EXPIRED_POINTS_DETAILS,
      code: REPORT_CODE.EXPIRED_POINTS_DETAILS,
      name: "Expired Points Details Report",
      description:
        "This report shows the points that have expired in a period of time per member on a partner level.",
      tier: ReportTier.PREMIUM,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.UPCOMING_POINTS_EXPIRATION,
    {
      id: ReportIds.UPCOMING_POINTS_EXPIRATION,
      code: REPORT_CODE.UPCOMING_POINTS_EXPIRATION,
      name: "Upcoming Points Expiration",
      description: "Summary on the points that will expire per partner.",
      tier: ReportTier.PREMIUM,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.FAILED_PARTNER_REWARD_TRANSACTIONS,
    {
      id: ReportIds.FAILED_PARTNER_REWARD_TRANSACTIONS,
      code: REPORT_CODE.FAILED_PARTNER_REWARD_TRANSACTIONS,
      name: "Failed to Process Partner Reward Transactions",
      description:
        "Report displaying the records provided by the partner in the transaction file that failed to be processed.",
      tier: ReportTier.PREMIUM,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.ENROLLMENT,
    {
      id: ReportIds.ENROLLMENT,
      code: REPORT_CODE.ENROLLMENT,
      name: "Enrolment Report",
      description:
        "This report shows the date of registration, the date of the first transaction posted and the first time login.",
      tier: ReportTier.PREMIUM,
      isVisible: true,
    },
  ],
  [
    ReportIds.ENROLLMENT_SUMMARY,
    {
      id: ReportIds.ENROLLMENT_SUMMARY,
      code: REPORT_CODE.ENROLLMENT_SUMMARY,
      name: "Enrollment Summary Report",
      description:
        "The report displays the total number of member's registered per partner.",
      tier: ReportTier.PREMIUM,
      isVisible: true,
    },
  ],
  [
    ReportIds.ADHOC_POINTS_ADJUSTMENT,
    {
      id: ReportIds.ADHOC_POINTS_ADJUSTMENT,
      code: REPORT_CODE.ADHOC_POINTS_ADJUSTMENT,
      name: "Ad-hoc Points Adjustment Report",
      description:
        "Report of ad-hoc reward and redemption transactions carried out through the call center",
      tier: ReportTier.PREMIUM,
      isVisible: true,
    },
  ],
  [
    ReportIds.POINTS_SUMMARY_REPORT,
    {
      id: ReportIds.POINTS_SUMMARY_REPORT,
      code: REPORT_CODE.POINTS_SUMMARY_REPORT,
      name: "Points Summary Report",
      description:
        "Overview of all points-related activities across the bank's loyalty program.",
      tier: ReportTier.PREMIUM,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.AUDIT_TRAIL,
    {
      id: ReportIds.AUDIT_TRAIL,
      code: REPORT_CODE.AUDIT_TRAIL,
      name: "Audit Trail Report",
      description: "An audit trail of changes made throughout the platform",
      tier: ReportTier.ADMIN_MANAGEMENT,
      isVisible: false,
    },
  ],
  [
    ReportIds.AUDIT_TRAIL_USERS,
    {
      id: ReportIds.AUDIT_TRAIL_USERS,
      code: REPORT_CODE.AUDIT_TRAIL_USERS,
      name: "Audit Trail Users",
      description: "This report shows the audit trail for the users",
      tier: ReportTier.ADMIN_MANAGEMENT,
      isVisible: false,
    },
  ],
  [
    ReportIds.PARTNER_SUMMARY_PENDING_TRANSACTIONS,
    {
      id: ReportIds.PARTNER_SUMMARY_PENDING_TRANSACTIONS,
      code: REPORT_CODE.PARTNER_SUMMARY_PENDING_TRANSACTIONS,
      name: "Partner Summary Pending Transaction Report",
      description:
        "Summary report on partner pending transactions awaiting release.",
      tier: ReportTier.ADMIN_MANAGEMENT,
      isVisible: false,
    },
  ],
  [
    ReportIds.ACTIVE_USERS_PER_PARTNER,
    {
      id: ReportIds.ACTIVE_USERS_PER_PARTNER,
      code: REPORT_CODE.ACTIVE_USERS_PER_PARTNER,
      name: "Active Users per Partner Report",
      description: "Active Users per Partner Report",
      tier: ReportTier.ADMIN_MANAGEMENT,
      isVisible: false,
    },
  ],
  [
    ReportIds.AUDIT_TRAIL_COMPREHENSIVE,
    {
      id: ReportIds.AUDIT_TRAIL_COMPREHENSIVE,
      code: REPORT_CODE.AUDIT_TRAIL_COMPREHENSIVE,
      name: "Audit Trail Comprehensive Report",
      description:
        "An audit trail of changes made throughout the platform and front-end applications",
      tier: ReportTier.ADMIN_MANAGEMENT,
      isVisible: true,
    },
  ],
  [
    ReportIds.ADMINS_LIST,
    {
      id: ReportIds.ADMINS_LIST,
      code: REPORT_CODE.ADMINS_LIST,
      name: "Admins List Report",
      description:
        "This report shows the list of admins for specific partner(s).",
      tier: ReportTier.ADMIN_MANAGEMENT,
      isVisible: true,
    },
  ],
  [
    ReportIds.PERIOD_TOTALS_BY_PARTNER,
    {
      id: ReportIds.PERIOD_TOTALS_BY_PARTNER,
      code: REPORT_CODE.PERIOD_TOTALS_BY_PARTNER,
      name: "Period Totals By Partner",
      description:
        "This report shows the activity totals by Partner.<br> Reversal transactions are included in the total count of redeem and reward transaction.",
      tier: ReportTier.ACTIVITY_SUMMARY,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.PERIOD_TOTALS_BY_MEMBER,
    {
      id: ReportIds.PERIOD_TOTALS_BY_MEMBER,
      code: REPORT_CODE.PERIOD_TOTALS_BY_MEMBER,
      name: "Period Totals By Member",
      description: "This report shows the activity totals by Member.",
      tier: ReportTier.ACTIVITY_SUMMARY,
      isVisible: false,
    },
  ],
  [
    ReportIds.PERIOD_TOTALS_BY_BRANCH,
    {
      id: ReportIds.PERIOD_TOTALS_BY_BRANCH,
      code: REPORT_CODE.PERIOD_TOTALS_BY_BRANCH,
      name: "Period Totals By Branch",
      description: "This report shows the activity totals by Branch.",
      tier: ReportTier.ACTIVITY_SUMMARY,
      isVisible: false,
    },
  ],
  [
    ReportIds.PERIOD_TOTALS_BY_NETWORK,
    {
      id: ReportIds.PERIOD_TOTALS_BY_NETWORK,
      code: REPORT_CODE.PERIOD_TOTALS_BY_NETWORK,
      name: "Period Totals By Network",
      description: "This report shows the activity totals by Network.",
      tier: ReportTier.ACTIVITY_SUMMARY,
      isVisible: false,
    },
  ],
  [
    ReportIds.BRANCH_POINTS_TRANSACTIONS,
    {
      id: ReportIds.BRANCH_POINTS_TRANSACTIONS,
      code: REPORT_CODE.BRANCH_POINTS_TRANSACTIONS,
      name: "Branch Points Transactions",
      description: "Report on total store transactions in points",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.TRANSACTIONS,
    {
      id: ReportIds.TRANSACTIONS,
      code: REPORT_CODE.TRANSACTIONS,
      name: "Transactions Report",
      description: "Report for all transactions conducted",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.BRANCH_TRANSACTIONS_IN_AMOUNT,
    {
      id: ReportIds.BRANCH_TRANSACTIONS_IN_AMOUNT,
      code: REPORT_CODE.BRANCH_TRANSACTIONS_IN_AMOUNT,
      name: "Branch Transactions in Amount - To Fix",
      description: "Report on total branch transactions in sales amount",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.BRANCH_TRANSACTIONS_IN_AMOUNT,
    {
      id: ReportIds.BRANCH_TRANSACTIONS_IN_AMOUNT,
      code: REPORT_CODE.LOYALTY_PROGRAM_COMPARISON,
      name: "Loyalty Programs Comparison - To Fix",
      description: "Multiple programs performance comparison",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.SEGMENT_COMPARISON,
    {
      id: ReportIds.SEGMENT_COMPARISON,
      code: REPORT_CODE.SEGMENT_COMPARISON,
      name: "Segment Comparison - To Fix",
      description: "Multiple segments loyalty performance comparison",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.COUNTRY_COMPARISON,
    {
      id: ReportIds.COUNTRY_COMPARISON,
      code: REPORT_CODE.COUNTRY_COMPARISON,
      name: "Country Comparison - To Fix",
      description: "Multiple countries loyalty performance comparison",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.ITEM_COMPARISON,
    {
      id: ReportIds.ITEM_COMPARISON,
      code: REPORT_CODE.ITEM_COMPARISON,
      name: "Item Comparison - To Fix",
      description: "Multiple items loyalty performance comparison",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.STAFF_COMPARISON,
    {
      id: ReportIds.STAFF_COMPARISON,
      code: REPORT_CODE.STAFF_COMPARISON,
      name: "Staff Comparison - To Fix",
      description: "Multiple staff loyalty performance comparison",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.ACCESS_RIGHT,
    {
      id: ReportIds.ACCESS_RIGHT,
      code: REPORT_CODE.ACCESS_RIGHTS,
      name: "Access Rights Report",
      description:
        "A report of all partner users who have access to the platform",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.REMOVED_CUSTOMERS_OR_PRODUCTS,
    {
      id: ReportIds.REMOVED_CUSTOMERS_OR_PRODUCTS,
      code: REPORT_CODE.REMOVED_CUSTOMERS_OR_PRODUCTS,
      name: "Removed Customers or Products - To Fix",
      description:
        "A report of all customers or products who have been removed from the program",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.BLOCKED_MEMBER_ACCOUNTS,
    {
      id: ReportIds.BLOCKED_MEMBER_ACCOUNTS,
      code: REPORT_CODE.BLOCKED_MEMBER_ACCOUNTS,
      name: "Blocked Member Accounts - To Fix",
      description:
        "A report of all members with blocked accounts due to failed log ins",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.MEMBERS_WITH_DEFAULT_PIN_CODES,
    {
      id: ReportIds.MEMBERS_WITH_DEFAULT_PIN_CODES,
      code: REPORT_CODE.MEMBERS_WITH_DEFAULT_PIN_CODE,
      name: "Members with default PIN codes - To Fix",
      description: "A report of all customers with PIN codes set to default",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.TOTAL_BALANCE_RECONCILIATION,
    {
      id: ReportIds.TOTAL_BALANCE_RECONCILIATION,
      code: REPORT_CODE.TOTAL_BALANCE_RECONCILIATION,
      name: "Total Balances Reconciliation",
      description:
        "This report shows the activity totals from start to date. Current Balances and Net Points Activity must be equal.",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.REDEMPTIONS_BY_ID,
    {
      id: ReportIds.REDEMPTIONS_BY_ID,
      code: REPORT_CODE.REDEMPTIONS_BY_ID,
      name: "Redemptions Report by ID - To Fix",
      description: "This report shows the redemptions by transaction",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.POINTS_REDEEMED_AT_SUPPLIER,
    {
      id: ReportIds.POINTS_REDEEMED_AT_SUPPLIER,
      code: REPORT_CODE.POINTS_REDEEMED_AT_SUPPLIER,
      name: "Points Redeemed at Supplier - To Fix",
      description: "This report shows the points redeemed at the supplier",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.SURVEY_PERFORMANCE,
    {
      id: ReportIds.SURVEY_PERFORMANCE,
      code: REPORT_CODE.SURVEY_PERFORMANCE,
      name: "Survey Performance Report",
      description: "This report shows the performance of a specific Survey",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: true,
      isAsync: true,
    },
  ],
  [
    ReportIds.MEMBER_BALANCE_VS_POINTS_REWARDED,
    {
      id: ReportIds.MEMBER_BALANCE_VS_POINTS_REWARDED,
      code: REPORT_CODE.MEMBER_BALANCE_VS_POINTS_REWARDED,
      name: "Member balance vs Points Rewarded - Points Redeemed",
      description:
        "The report will show the discrepancies between the member balance and the points rewarded - points redeemed.",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.BRANCH_POINTS_TRANSACTIONS_BY_POSTED_DATE,
    {
      id: ReportIds.BRANCH_POINTS_TRANSACTIONS_BY_POSTED_DATE,
      code: REPORT_CODE.BRANCH_POINTS_TRANSACTIONS_BY_POSTED_DATE,
      name: "Branch Points Transaction Report by Posted Date",
      description: "Branch Points Transaction Report by Posted Date",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.REDEMPTION_BY_PARTNER_USED_POINTS,
    {
      id: ReportIds.REDEMPTION_BY_PARTNER_USED_POINTS,
      code: REPORT_CODE.REDEMPTION_BY_PARTNER_USED_POINTS,
      name: "Redemptions by Partner Used Points Vs Other Partners",
      description:
        "This report provides a detailed breakdown of redemption transactions, showing how points are used across different partners.",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: true,
    },
  ],
  [
    ReportIds.REDEMPTION_BY_PARTNER_USED_POINTS_BY_ITEM,
    {
      id: ReportIds.REDEMPTION_BY_PARTNER_USED_POINTS_BY_ITEM,
      code: REPORT_CODE.REDEMPTION_BY_PARTNER_USED_POINTS_BY_ITEM,
      name: "Redemption by Partner Used Points by Item",
      description: "Redemption by Partner Used Points by Item",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.ONLINE_VOUCHER_REDEMPTION,
    {
      id: ReportIds.ONLINE_VOUCHER_REDEMPTION,
      code: REPORT_CODE.ONLINE_VOUCHERS_REDEMPTION,
      name: "Online Vouchers Redemption Report",
      nameInNotification: "Online Vouchers Redemption",
      description:
        "Summary on redemption transactions performed for Online Vouchers",
      tier: ReportTier.BLU_INTERNAL,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.CATALOGUE_LISTING_PER_PARTNER,
    {
      id: ReportIds.CATALOGUE_LISTING_PER_PARTNER,
      code: REPORT_CODE.CATALOGUE_LISTING_PER_PARTNER,
      name: "Catalogue Listing per Partner",
      description:
        "List of items as vouchers displayed on a specific partner's website/apps.",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.SUPPLIER_INVOICE_RECONCILIATION,
    {
      id: ReportIds.SUPPLIER_INVOICE_RECONCILIATION,
      code: REPORT_CODE.SUPPLIER_INVOICE_RECONCILIATION,
      name: "Supplier Invoice Reconciliation",
      description: "Summary on the supplier invoice reconciliation.",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.REDEMPTION_BY_PARTNER,
    {
      id: ReportIds.REDEMPTION_BY_PARTNER,
      code: REPORT_CODE.REDEMPTION_BY_PARTNER,
      name: "Redemption by Partner",
      description: "Summary of the redemption by partner.",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.ALINMA_MONTHLY_REDEMPTION,
    {
      id: ReportIds.ALINMA_MONTHLY_REDEMPTION,
      code: REPORT_CODE.ALINMA_MONTHLY_REDEMPTION,
      name: "Alinma Monthly Redemption",
      description: "Summary on Alinma redemption for the given month.",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.ITEM_REDEMPTION_BY_PARTNER,
    {
      id: ReportIds.ITEM_REDEMPTION_BY_PARTNER,
      code: REPORT_CODE.ITEM_REDEMPTION_BY_PARTNER,
      name: "Item Redemption by Partner",
      description: "Summary on item redemption by partner in period.",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.PROFIT_AND_LOSS_BY_SUPPLIER,
    {
      id: ReportIds.PROFIT_AND_LOSS_BY_SUPPLIER,
      code: REPORT_CODE.PROFIT_AND_LOSS_BY_SUPPLIER,
      name: "P&L by Supplier",
      description: "Summary of the profit and loss by suppliers.",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.OFFLINE_VOUCHER_REDEMPTION,
    {
      id: ReportIds.OFFLINE_VOUCHER_REDEMPTION,
      code: REPORT_CODE.OFFLINE_VOUCHER_REPORT,
      name: "Offline Voucher Report",
      description:
        "Summary on redemption transactions performed for Offline Vouchers",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: true,
    },
  ],
  [
    ReportIds.CATALOGUE_REDEMPTION_DETAILS,
    {
      id: ReportIds.CATALOGUE_REDEMPTION_DETAILS,
      code: REPORT_CODE.CATALOGUE_REDEMPTION_DETAILS,
      name: "Catalogue Redemption Details Report for P&L and Invoicing",
      description:
        "This report shows all the catalogue redemption transactions along with the pricing details for P&L and invoicing purposes.",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: false,
    },
  ],
  [
    ReportIds.CATALOGUE_ITEMS_PER_PARTNER,
    {
      id: ReportIds.CATALOGUE_ITEMS_PER_PARTNER,
      code: REPORT_CODE.CATALOGUE_ITEMS_PER_PARTNER,
      name: "Catalogue Items per Partner",
      description:
        "Report displaying the list of product/vouchers offered per partner (display channel)",
      tier: ReportTier.BLU_INTERNAL,
      isVisible: true,
      isAsync: true,
    },
  ],
  [
    ReportIds.REDEMPTION_BY_ITEM,
    {
      id: ReportIds.REDEMPTION_BY_ITEM,
      code: REPORT_CODE.REDEMPTION_BY_ITEM,
      name: "Redemption By Item",
      description:
        "This report shows a summary of redemptions by item performed.",
      isScheduled: true,
      tier: ReportTier.BLU_INTERNAL,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.VOUCHER_REDEMPTIONS,
    {
      id: ReportIds.VOUCHER_REDEMPTIONS,
      code: REPORT_CODE.VOUCHER_REDEMPTIONS,
      name: "Redemption Vouchers",
      description:
        "This report shows a summary of redemption transactions performed for Vouchers.",
      isScheduled: true,
      tier: ReportTier.BLU_INTERNAL,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.REDEMPTIONS_OVERVIEW,
    {
      id: ReportIds.REDEMPTIONS_OVERVIEW,
      code: REPORT_CODE.REDEMPTION_OVERVIEW,
      name: "Redemption Overview",
      description:
        "This report shows a summary of redemption transactions performed.",
      isScheduled: true,
      isAsync: true,
      tier: ReportTier.BLU_INTERNAL,
      isVisible: true,
    },
  ],
  [
    ReportIds.DETAILED_IN_STORE_REDEMPTION_TRANSACTIONS,
    {
      id: ReportIds.DETAILED_IN_STORE_REDEMPTION_TRANSACTIONS,
      code: REPORT_CODE.DETAILED_IN_STORE_REDEMPTION_TRANSACTIONS,
      name: "Detailed In-Store Redemption Transactions",
      description:
        "This report shows in detail all redemption and reversal transactions that took place between two time frames at specific merchants",
      isScheduled: true,
      isAsync: true,
      tier: ReportTier.BLU_INTERNAL,
      isVisible: true,
    },
  ],
  [
    ReportIds.PROFIT_AND_LOSS_BY_PARTNER,
    {
      id: ReportIds.PROFIT_AND_LOSS_BY_PARTNER,
      code: REPORT_CODE.PROFIT_AND_LOSS_BY_PARTNER,
      name: "Items P&L Report per Partner",
      description:
        "The report will be used by the admins to track the P&L for item redemptions (physical item, vouchers, and Promo Code) on a partner level.",
      isScheduled: true,
      isAsync: true,
      tier: ReportTier.BLU_INTERNAL,
      isVisible: true,
    },
  ],
  [
    ReportIds.PROFIT_AND_LOSS_FOR_ITEMS,
    {
      id: ReportIds.PROFIT_AND_LOSS_FOR_ITEMS,
      code: REPORT_CODE.PROFIT_AND_LOSS_FOR_ITEMS,
      name: "Profit and Loss for Items",
      description:
        "This report shows the profit and loss within a period of time per item on a partner level.",
      tier: ReportTier.BLU_INTERNAL,
      isScheduled: true,
      isAsync: true,
      isVisible: false,
    },
  ],
  [
    ReportIds.PENDING_REVERSAL_REWARD_TRANSACTIONS,
    {
      id: ReportIds.PENDING_REVERSAL_REWARD_TRANSACTIONS,
      code: REPORT_CODE.PENDING_REVERSAL_REWARD_TRANSACTIONS,
      name: "Pending Reversal Reward Transactions",
      description:
        "This report shows the list of all pending reversal reward transactions.",
      tier: ReportTier.BLU_INTERNAL,
      isScheduled: false,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.FINANCE_POINTS_REWARDED,
    {
      id: ReportIds.FINANCE_POINTS_REWARDED,
      code: REPORT_CODE.POINTS_REWARDED_REPORT_FOR_BUSINESS,
      name: "Points Rewarded Report for Finance",
      description: "Summary of the Points Rewarded for Finance",
      tier: ReportTier.ACCOR_REPORTS,
      isVisible: true,
    },
  ],
  [
    ReportIds.POINTS_REWARDED_BUSINESS,
    {
      id: ReportIds.POINTS_REWARDED_BUSINESS,
      code: REPORT_CODE.POINTS_REWARDED_REPORT_FOR_BUSINESS,
      name: "Points Rewarded Report for Business",
      description: "Summary of the Points Rewarded for Business",
      tier: ReportTier.ACCOR_REPORTS,
      isVisible: true,
    },
  ],
  [
    ReportIds.PENDING_NEGATIVE_REWARD_TRANSACTIONS,
    {
      id: ReportIds.PENDING_NEGATIVE_REWARD_TRANSACTIONS,
      code: REPORT_CODE.PENDING_NEGATIVE_REWARD_TRANSACTIONS,
      name: "Pending Negative Reward Transactions",
      description:
        "This report shows the list of all pending negative reward transactions.",
      tier: ReportTier.PENDING_REWARD_REPORTS,
      isScheduled: false,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.BALANCE_PENDING_MEMBERS_LIST,
    {
      id: ReportIds.BALANCE_PENDING_MEMBERS_LIST,
      code: REPORT_CODE.BALANCE_PENDING_MEMBERS_LIST,
      name: "Balance Pending Members List",
      description:
        "This report shows the list of all members with pending negative reward transactions.",
      tier: ReportTier.PENDING_REWARD_REPORTS,
      isScheduled: false,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.GENERIC_MARKET_PLACE_ORDERS,
    {
      id: ReportIds.GENERIC_MARKET_PLACE_ORDERS,
      code: REPORT_CODE.GENERIC_MARKET_PLACE_ORDERS,
      name: "Generic Marketplace Orders",
      description:
        "This report shows the marketplace orders executed in a certain period of time",
      tier: ReportTier.MARKET_PLACE_REPORTS,
      isScheduled: false,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.ALL_CATALOG_MARKET_PLACE_ORDERS,
    {
      id: ReportIds.ALL_CATALOG_MARKET_PLACE_ORDERS,
      code: REPORT_CODE.ALL_CATALOG_MARKET_PLACE_ORDERS,
      name: "All Catalog Marketplace Orders",
      description:
        "This report shows the details of the marketplace orders executed in a certain period of time.",
      tier: ReportTier.MARKET_PLACE_REPORTS,
      isScheduled: false,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.MARKET_PLACE_AFFILIATE_PROGRAM_ORDERS,
    {
      id: ReportIds.MARKET_PLACE_AFFILIATE_PROGRAM_ORDERS,
      code: REPORT_CODE.MARKET_PLACE_AFFILIATE_PROGRAM_ORDERS,
      name: "Marketplace Affiliate Program Orders",
      description:
        "This report shows the conversion orders executed in a period of time to participating affiliate programs.",
      tier: ReportTier.MARKET_PLACE_REPORTS,
      isScheduled: false,
      isAsync: true,
      isVisible: true,
    },
  ],
  [
    ReportIds.ONLINE_VOUCHERS_REDEMPTION_MARKETPLACE_PARTNERS,
    {
      id: ReportIds.ONLINE_VOUCHERS_REDEMPTION_MARKETPLACE_PARTNERS,
      code: REPORT_CODE.ONLINE_VOUCHERS_REDEMPTION_MARKETPLACE_PARTNERS,
      name: "Online Vouchers Redemption - Marketplace Partners",
      nameInNotification: "Online Vouchers Redemption - Marketplace Partners",
      description:
        "Details on online voucher redemption orders performed through marketplace partners.",
      tier: ReportTier.BLU_INTERNAL,
      isScheduled: false,
      isAsync: true,
      isVisible: true,
    },
  ],
]);

export const reportTiersSummary: ReportTierSummary[] = [
  {
    id: 1,
    tier: ReportTier.BASIC,
    name: "Basic reports",
    permissionName: PermissionNames.VIEW_BASIC_REPORTS,
    reportList: getTierReports(ReportTier.BASIC),
  },
  {
    id: 2,
    tier: ReportTier.PREMIUM,
    name: "Premium reports",
    permissionName: PermissionNames.VIEW_PREMIUM_REPORTS,
    reportList: getTierReports(ReportTier.PREMIUM),
  },
  {
    id: 4,
    tier: ReportTier.ADMIN_MANAGEMENT,
    name: "Admin management and audit reports",
    permissionName: PermissionNames.VIEW_ADMIN_MANAGEMENT_AND_AUDIT_REPORTS,
    reportList: getTierReports(ReportTier.ADMIN_MANAGEMENT),
  },
  {
    id: 5,
    tier: ReportTier.ACTIVITY_SUMMARY,
    name: "Activity summary reports",
    permissionName: PermissionNames.VIEW_ACTIVITY_SUMMARY_REPORTS,
    reportList: getTierReports(ReportTier.ACTIVITY_SUMMARY),
  },
  {
    id: 6,
    tier: ReportTier.REDEMPTION,
    name: "Redemption reports",
    permissionName: PermissionNames.VIEW_REDEMPTION_REPORTS,
    reportList: getTierReports(ReportTier.REDEMPTION),
  },
  {
    id: 7,
    tier: ReportTier.BLU_INTERNAL,
    name: "BLU internal reports",
    permissionName: PermissionNames.VIEW_BLU_INTERNAL_REPORTS,
    reportList: getTierReports(ReportTier.BLU_INTERNAL),
  },
  {
    id: 8,
    tier: ReportTier.ACCOR_REPORTS,
    name: "Accor Reports",
    permissionName: PermissionNames.VIEW_ACCOR_REPORTS,
    reportList: getTierReports(ReportTier.ACCOR_REPORTS),
  },
  {
    id: 9,
    tier: ReportTier.PENDING_REWARD_REPORTS,
    name: "Pending Rewards Reports",
    permissionName: PermissionNames.VIEW_PENDING_REWARDS_REPORTS,
    reportList: getTierReports(ReportTier.PENDING_REWARD_REPORTS),
  },
  {
    id: 10,
    tier: ReportTier.MARKET_PLACE_REPORTS,
    name: "Marketplace Reports",
    permissionName: PermissionNames.VIEW_MARKET_PLACE_REPORTS,
    reportList: getTierReports(ReportTier.MARKET_PLACE_REPORTS),
  },
];

function getTierReports(reportTier: ReportTier) {
  const result: ReportInfo[] = [];
  ReportInfoMap.forEach((report: ReportInfo) => {
    if (report.tier === reportTier && report.isVisible) {
      result.push(report);
    }
  });
  return result.sort((a: ReportInfo, b: ReportInfo) => (a.id < b.id ? -1 : 1));
}

/**
 * Calculates and returns the Date representing the start of the day (00:00:00)
 * exactly one month prior to the current moment when the function is called.
 *
 * @returns {Date} The calculated Date object.
 */
export function getReportStartDate(): Date {
  return moment()
    .subtract(1, "month") // Go back one month from now
    .startOf("day") // Set the time to the beginning of that day (00:00:00.000)
    .toDate();
}

export function getDateOfFirstDayOfLastMonth() {
  return moment().subtract(1, "month").startOf("month").startOf("day").toDate();
}
